import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import { initScrollAnimations, cleanupScrollAnimations } from '../utils/scrollAnimations';
import '../styles/privateevents.css';
import '../styles/animations.css';

const PrivateEvents = () => {
  const { language } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize scroll animations
  useEffect(() => {
    const scrollObserver = initScrollAnimations();
    return () => {
      cleanupScrollAnimations(scrollObserver);
    };
  }, []);

  return (
    <div className="private-events-page">
      <Header isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section 
        className="private-events-hero"
        style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/PawilonChristmas.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="private-events-hero-content">
          <h1 className="private-events-hero-title">{t('private.hero.title', language)}</h1>
          <p className="private-events-hero-subtitle">{t('private.hero.subtitle', language)}</p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="private-events-intro scroll-animate">
        <div className="private-events-intro-content">
          <h2 className="private-events-intro-title scroll-animate delay-100">{t('private.intro.title', language)}</h2>
          <p className="private-events-intro-description scroll-animate delay-200">
            {t('private.intro.description', language)}
          </p>
          <div className="private-events-intro-features">
            <div className="private-events-intro-feature">
              <h4>{t('private.features.mainHall', language)}</h4>
              <p>{language === 'pl' ? 'Główna sala jadalna - do 40 gości siedzących' : 'Main dining hall - up to 40 seated guests'}</p>
            </div>
            <div className="private-events-intro-feature">
              <h4>{language === 'pl' ? 'Górna Sala' : 'Upper Room'}</h4>
              <p>{language === 'pl' ? 'Idealna do układania stołów bufetowych lub baru' : 'Perfect for laying out grazing tables or a bar'}</p>
            </div>
            <div className="private-events-intro-feature">
              <h4>{language === 'pl' ? 'Taras' : 'Rooftop'}</h4>
              <p>{language === 'pl' ? 'Uroczystości na świeżym powietrzu (60 osób stojących)' : 'Open-air celebrations (60 standing)'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions Section */}
      <section className="private-events-occasions scroll-animate">
        <div className="private-events-occasions-content">
          <div className="private-events-occasions-left scroll-animate delay-100">
            <h2 className="private-events-occasions-title">{t('private.events.title', language)}</h2>
            <h3 className="private-events-occasions-subtitle">{language === 'pl' ? 'Każdą Okazję' : 'Every Occasion'}</h3>
            <p className="private-events-occasions-description">
              {language === 'pl' ? 'Pawilon Krzyżanowskiego zapewnia idealne tło dla najważniejszych chwil życia. Nasze wszechstronne przestrzenie dostosowują się do Twojej wizji, niezależnie od tego, czy organizujesz kameralne spotkanie, czy wielką uroczystość.' : 'Pawilon Krzyżanowskiego provides the ideal backdrop for life\'s most important moments. Our versatile spaces adapt to your vision, whether you\'re hosting an intimate gathering or a grand celebration.'}
            </p>
            <p className="private-events-occasions-description">
              {language === 'pl' ? 'Od eleganckich wesel po wydarzenia korporacyjne, nasz historyczny obiekt przekształca się, aby dopasować się do Twojego unikalnego stylu i wymagań.' : 'From elegant weddings to corporate events, our historic venue transforms to match your unique style and requirements.'}
            </p>
            <Link to="/Contact" className="private-events-occasions-button">{t('private.contact.button', language)}</Link>
          </div>
          <div className="private-events-occasions-right">
            <div className="private-events-occasions-gallery">
              <div className="private-events-occasions-column">
                <div className="private-events-occasions-image-container">
                  <img src="/assets/PawilonWedding.png" alt="Personal Celebrations" className="private-events-occasions-image" />
                </div>
                <div className="private-events-occasions-content">
                  <div className="private-events-occasions-text-container">
                    <span className="private-events-occasions-text-en">{language === 'pl' ? 'Rodzinne' : 'Personal'}</span>
                    <span className="private-events-occasions-text-jp">{language === 'pl' ? 'Uroczystości' : 'Celebrations'}</span>
                  </div>
                </div>
              </div>
              
              <div className="private-events-occasions-column">
                <div className="private-events-occasions-image-container">
                  <img src="/assets/PawilonConference.png" alt="Professional Events" className="private-events-occasions-image" />
                </div>
                <div className="private-events-occasions-content">
                  <div className="private-events-occasions-text-container">
                    <span className="private-events-occasions-text-en">{language === 'pl' ? 'Biznesowe' : 'Professional'}</span>
                    <span className="private-events-occasions-text-jp">{language === 'pl' ? 'Wydarzenia' : 'Events'}</span>
                  </div>
                </div>
              </div>
              
              <div className="private-events-occasions-column">
                <div className="private-events-occasions-image-container">
                  <img src="/assets/PawilonChristmas.png" alt="Cultural Occasions" className="private-events-occasions-image" />
                </div>
                <div className="private-events-occasions-content">
                  <div className="private-events-occasions-text-container">
                    <span className="private-events-occasions-text-en">{language === 'pl' ? 'Sezonowe' : 'Cultural'}</span>
                    <span className="private-events-occasions-text-jp">{language === 'pl' ? 'Przyjęcia' : 'Occasions'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking & Amenities Section */}
      <section className="private-events-booking">
        <div className="private-events-booking-content">
          <div className="private-events-booking-left">
            <h2 className="private-events-booking-title">{t('private.booking.title', language)}</h2>
            <h3 className="private-events-booking-subtitle">{t('private.booking.subtitle', language)}</h3>
            <p className="private-events-booking-description">
              {t('private.booking.description', language)}
            </p>
            <div className="private-events-booking-options">
              <div className="private-events-booking-option">
                <h4>{t('private.booking.venueOnly', language)}</h4>
                <p>{t('private.booking.venueOnlyDesc', language)}</p>
              </div>
              <div className="private-events-booking-option">
                <h4>{t('private.booking.fullService', language)}</h4>
                <p>{t('private.booking.fullServiceDesc', language)}</p>
              </div>
            </div>
          </div>
          <div className="private-events-booking-right">
            <h2 className="private-events-booking-title">{t('private.amenities.title', language)}</h2>
            <h3 className="private-events-booking-subtitle">{t('private.amenities.subtitle', language)}</h3>
            <p className="private-events-booking-description">
              {t('private.amenities.description', language)}
            </p>
            <div className="private-events-booking-amenities">
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.fullAccess', language)}</h4>
                <p>{t('private.amenities.fullAccessDesc', language)}</p>
              </div>
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.soundSystem', language)}</h4>
                <p>{t('private.amenities.soundSystemDesc', language)}</p>
              </div>
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.flexibleLayouts', language)}</h4>
                <p>{t('private.amenities.flexibleLayoutsDesc', language)}</p>
              </div>
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.avEquipment', language)}</h4>
                <p>{t('private.amenities.avEquipmentDesc', language)}</p>
              </div>
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.climateControl', language)}</h4>
                <p>{t('private.amenities.climateControlDesc', language)}</p>
              </div>
              <div className="private-events-booking-amenity">
                <h4>{t('private.amenities.lighting', language)}</h4>
                <p>{t('private.amenities.lightingDesc', language)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Call to Action Section */}
      <section className="private-events-cta">
        <div className="private-events-cta-content">
          <h2 className="private-events-cta-title">{t('private.cta.title', language)}</h2>
          <p className="private-events-cta-description">
            {t('private.cta.description', language)}
          </p>
                  <div className="private-events-cta-buttons">
                    <Link to="/contact" className="private-events-cta-button">{t('private.cta.contact', language)}</Link>
                    <Link to="/events" className="private-events-cta-link">{t('private.cta.viewEvents', language)}</Link>
                  </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateEvents;